import React from 'react';

import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	isForgeConnectorProductKey,
	SMARTLINKS_CONNECTOR_SOURCE,
	type ThirdPartyConfigsBootstrap,
} from '../../../common/constants/schemas/3p-config';
import { ThirdPartyConfigs } from '../../constants/3p-product-configs';
import {
	is3pProductKey,
	type ProductKeys,
	ProductKeys3P,
	PRODUCTS_ORDER,
} from '../../constants/products';
import { OAuthBanner } from '../../ui/oauth-banner';
import { shouldShowOAuthBanner } from '../../ui/oauth-banner/utils';
import type { OAuthSource } from '../events/oauth-events';

interface isProductSmartlinkProps {
	productKey?: string; // loosely typed as string to accomodate for few cases which have this value as a string
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}

export const isProductSmartlink = ({ productKey, thirdPartyConfigs }: isProductSmartlinkProps): boolean => {
	const product3PConfig =
		productKey && is3pProductKey(productKey) ? thirdPartyConfigs[productKey] : null;
	return (
		product3PConfig?.connectorSources?.length === 1 &&
		product3PConfig.connectorSources[0] === SMARTLINKS_CONNECTOR_SOURCE
	);
};

interface OAuthHandlersProps {
	product: ProductKeys;
	successFlagTitle: React.ReactNode;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	isOnSearchDialog?: boolean;
}
interface OauthHandlerFunctions {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
}

type useOAuthHandlers = (props: OAuthHandlersProps) => OauthHandlerFunctions;

interface BannerProps {
	isOnSearchDialog?: boolean;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
	isHello: boolean;
	useOAuthHandlers: useOAuthHandlers;
	fireAnalyticsEvent: (payload: any) => void;
}

export const OAuthContainer = ({
	isOnSearchDialog,
	thirdPartyConfigs,
	availableProducts,
	isHello,
	useOAuthHandlers,
	fireAnalyticsEvent,
}: BannerProps): React.JSX.Element | null => {
	// Check if user has opted out of 3P OAuth banner
	if (fg('rovo_search_3p_banner_opt_out')) {
		return null;
	}

	let productOrder: ProductKeys[] = [];

	if (isOnSearchDialog) {
		productOrder = [
			ProductKeys3P.GoogleDrive,
			ProductKeys3P.Sharepoint,
			ProductKeys3P.Teams,
			ProductKeys3P.Slack,
			ProductKeys3P.Github,
			ProductKeys3P.Figma,
			ProductKeys3P.Gitbook,
		];
	} else {
		const requiredProducts: ProductKeys[] = [];

		ThirdPartyConfigs.forEach((_, productKey3P) => {
			if (ThirdPartyConfigs.get(productKey3P)?.requiresOauth) {
				requiredProducts.push(productKey3P);
			}
		});

		// Filter and order static products based on PRODUCTS_ORDER
		const staticProducts = PRODUCTS_ORDER.filter((product) => requiredProducts.includes(product));

		// Add Forge connectors that need OAuth (ordered after static products)
		const forgeConnectors = availableProducts.filter(
			(product) =>
				isForgeConnectorProductKey(product) &&
				thirdPartyConfigs[product]?.userNeedsOAuth &&
				thirdPartyConfigs[product]?.outboundAuthUrl,
		);

		productOrder = [...staticProducts, ...forgeConnectors];
	}
	const productsToBeDisplayed = productOrder.filter((product) =>
		availableProducts.includes(product),
	);

	let displayedProduct;
	let displayedProduct3PConfig;

	for (const product of productsToBeDisplayed) {
		const config = thirdPartyConfigs[product as ProductKeys3P];
		if (config && config.userNeedsOAuth && shouldShowOAuthBanner(product, isHello)) {
			displayedProduct = product;
			displayedProduct3PConfig = config;
			break;
		}
	}

	const outboundAuthUrl = displayedProduct3PConfig?.outboundAuthUrl;
	// a little redundant way of getting this value, but just making use of common function
	const isSmartlink = isProductSmartlink({ productKey: displayedProduct, thirdPartyConfigs });

	return displayedProduct && outboundAuthUrl ? (
		isOnSearchDialog ? (
			<OAuthBanner
				product={displayedProduct}
				outboundAuthUrl={outboundAuthUrl}
				useOAuthHandlers={useOAuthHandlers}
				isSmartlink={isSmartlink}
				isOnSearchDialog={isOnSearchDialog}
				fireAnalyticsEvent={fireAnalyticsEvent}
			/>
		) : (
			<FlagsProvider>
				<OAuthBanner
					product={displayedProduct}
					outboundAuthUrl={outboundAuthUrl}
					useOAuthHandlers={useOAuthHandlers}
					isSmartlink={isSmartlink}
					fireAnalyticsEvent={fireAnalyticsEvent}
				/>
			</FlagsProvider>
		)
	) : null;
};
